import {
  extractSdkErrorDetail,
  getResponseStatus,
  PROTECTED_BRANCH,
  readConflictDetail,
  TAG_NAME_TAKEN,
} from "@istari-frontend/shared";
import type { PublishingConflictDetailDtoErrorCodeEnum } from "@istari/istari-client";

import { BranchingError } from "~/branching/model/branching-error";
import { PROTECTED_BRANCH_REASON } from "~/branching/model/protected-branch";

const NAME_TAKEN_MESSAGE = "Branch name already exists";

// Stopgap until the backend exposes a structured error code: some 403 responses
// carry archived-branch prose in `detail` without a 400/422 status.
function isArchivedBranchDetail(detail: string): boolean {
  return /archived/i.test(detail) && /cannot|can't|not allowed|rename|update/i.test(detail);
}

function clientErrorMessage(status: number, detail: string | undefined): string {
  if (detail) {
    return detail;
  }
  return status === 422 ? "The branch name could not be updated. Check the name and try again." : "Invalid request";
}

export type ToBranchingErrorOptions = {
  /**
   * How to read a 409 whose readable detail carries no `error_code`, as older registries send for a taken tag name.
   * An unreadable body (not JSON, or no detail) stays `conflict`.
   */
  uncodedConflict?: "name-taken";
};

/**
 * Map SDK/HTTP errors to a typed `BranchingError` for the domain port.
 */
export async function toBranchingError(cause: unknown, options: ToBranchingErrorOptions = {}): Promise<BranchingError> {
  if (cause instanceof BranchingError) {
    return cause;
  }

  const status = getResponseStatus(cause);
  if (status !== undefined) {
    const detail = await extractSdkErrorDetail(cause, { joinValidationDetail: true }).catch(() => undefined);

    if (status === 404) {
      return new BranchingError("not-found", detail ?? "Resource not found", cause);
    }
    if (status === 0 || status >= 500) {
      return new BranchingError("unavailable", detail ?? "Service unavailable", cause);
    }
    if (status === 409) {
      return toConflictError(cause, detail, options);
    }
    if (detail && isArchivedBranchDetail(detail)) {
      return new BranchingError("invalid", detail, cause);
    }
    if (status === 400 || status === 422) {
      return new BranchingError("invalid", clientErrorMessage(status, detail), cause);
    }
  }

  if (cause instanceof Error) {
    return new BranchingError("unknown", cause.message, cause);
  }
  return new BranchingError("unknown", "Unknown error", cause);
}

async function toConflictError(
  cause: unknown,
  detail: string | undefined,
  options: ToBranchingErrorOptions,
): Promise<BranchingError> {
  const conflictDetail = await readConflictDetail(cause);
  const errorCode = conflictDetail?.errorCode;
  if (errorCode === (PROTECTED_BRANCH satisfies PublishingConflictDetailDtoErrorCodeEnum)) {
    return new BranchingError("protected-branch", PROTECTED_BRANCH_REASON, cause);
  }
  const isReadableUncodedDetail = errorCode === undefined && (conflictDetail !== undefined || detail !== undefined);
  if (errorCode === TAG_NAME_TAKEN || (isReadableUncodedDetail && options.uncodedConflict === "name-taken")) {
    return new BranchingError("name-taken", NAME_TAKEN_MESSAGE, cause);
  }
  return new BranchingError("conflict", detail ?? "Request conflict", cause);
}
